import React from 'react';
import Type from 'prop-types';
import { TextField, Autocomplete } from '@mui/material';
import { fieldToTextField } from 'formik-mui';
import { t } from 'utils/i18n';

const getAutocompleteFieldProps = (props) => {
  const {
    field,
    form,
    onChange,
    onBlur,
    multiple,
    freeSolo,
    ...other
  } = props;

  const handleChange = (event, value, reason, details) => {
    if (field?.name && form?.setFieldValue) {
      form.setFieldValue(field.name, value);
    }

    if (onChange) {
      onChange(event, value, reason, details);
    }
  };

  const handleBlur = (event) => {
    if (field?.name && form?.setFieldTouched) {
      form.setFieldTouched(field.name, true);
    }

    if (onBlur) {
      onBlur(event);
    } else if (field?.onBlur) {
      field.onBlur(event);
    }
  };

  return {
    ...other,
    multiple,
    freeSolo,
    value: field?.value ?? (multiple ? [] : null),
    onChange: handleChange,
    onBlur: handleBlur,
  };
};

const normalizeHelperText = (helperText) => {
  if (helperText == null || typeof helperText === 'string') {
    return helperText;
  }

  if (typeof helperText === 'object') {
    const messages = Object.values(helperText).filter(
      (value) => typeof value === 'string',
    );

    return messages.length ? messages.join(' ') : undefined;
  }

  return String(helperText);
};

const AutocompleteField = ({ TextFieldProps, ...props }) => {
  const { error, helperText } = fieldToTextField(props);
  const { loadingText, noOptionsText, ...other } = getAutocompleteFieldProps(props);

  const mergedTextFieldProps = {
    ...AutocompleteField.defaultProps.TextFieldProps,
    ...TextFieldProps,
  };

  return (
    <Autocomplete
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      {...other}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            helperText={normalizeHelperText(helperText)}
            error={Boolean(error)}
            {...mergedTextFieldProps}
          />
        );
      }}
    />
  );
};

AutocompleteField.propTypes = {
  loadingText: Type.string,
  noOptionsText: Type.string,
  TextFieldProps: Type.shape({
    label: Type.string.isRequired,
    required: Type.bool,
    margin: Type.oneOf(['none', 'dense', 'normal']),
  }),
};

AutocompleteField.defaultProps = {
  loadingText: t('loading'),
  noOptionsText: t('notFound'),
  TextFieldProps: {
    margin: 'normal',
    required: false,
  },
};

export default AutocompleteField;
